import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useSnackbar } from "notistack";
import { ReactElement, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  findEarliestStartDate,
  tollFreeRegistrationSchema,
} from "@stw/yup-validators";

import {
  ComplianceRegistrationUsageType,
  CreateTollFreeRegistrationMutation,
  CreateTollFreeRegistrationMutationVariables,
  TollFreeOrgType,
} from "~frontend/__generated__/graphql";
import { AccentBox } from "~frontend/components/AccentBox";
import { Checkbox } from "~frontend/components/Checkbox";
import CollapsibleDrawer from "~frontend/components/CollapsibleDrawer";
import { FlexBox } from "~frontend/components/FlexBox";
import Jotform, {
  VERIFIED_TOLLFREE_JOTFORM_FORM_ID,
} from "~frontend/components/Jotform";
import { ExternalLink, LinkButton } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import {
  FormikDatePicker,
  FormikErrorBanner,
  FormikSelect,
  FormikTextField,
  submitErrorField,
} from "~frontend/components/forms";
import { FormikSearchableSelect } from "~frontend/components/forms/FormSelect";
import { CREATE_TOLL_FREE_REGISTRATION } from "~frontend/graphql/tollFreeRegistrations";
import { useTenant } from "~frontend/providers/LoggedInUserProvider";
import {
  useCurrentUser,
  useCurrentUserIsStaff,
  useFeatureFlag,
} from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { InputRow } from "~frontend/routes/staff/compliance-registrations/ten-dlc/TenDlcProvider";
import { palette } from "~frontend/theme";
import { STATE_MENU_OPTIONS } from "~frontend/util/stateMenuOptions";
import useMutation from "~frontend/util/useMutation";

import useOptOutPhraseList from "../campaignEditor/Script/useOptOutPhraseList";

const ORG_TYPE_MENU_OPTIONS: { label: string; value: TollFreeOrgType }[] = [
  {
    label: "527 Organization (Political Campaign, Party, or PAC)",
    value: TollFreeOrgType.Political,
  },
  {
    label: "501c3",
    value: TollFreeOrgType.C3,
  },
  {
    label: "501c4",
    value: TollFreeOrgType.C4,
  },
  {
    label: "501c5",
    value: TollFreeOrgType.C5,
  },
  {
    label: "Private Entity",
    value: TollFreeOrgType.Private,
  },
  {
    label: "Government",
    value: TollFreeOrgType.Government,
  },
];

const initialValues = {
  organizationName: "",
  orgType: "",
  displayName: "",
  ein: "",
  cvToken: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "US",
  website: "",

  contactFirstName: "",
  contactLastName: "",
  contactEmail: "",
  contactPhone: "",

  orgContactIsPrimaryContact: true,

  primaryContactFirstName: "",
  primaryContactLastName: "",
  primaryContactEmail: "",
  primaryContactPhone: "",

  createComplianceBundle: true,
  acknowledgedCompliance: false,

  messageVolume: 100,
  useCaseSummary: "",
  productionMessageContent: "",
  usageTypes: [] as ComplianceRegistrationUsageType[],
  privacyPolicyUrl: "",
  optInPageUrl: "",
  termsAndConditionsUrl: "",

  notes: "",

  [submitErrorField]: null,
};

export const COMPLIANCE_REGISTRATION_USAGE_TYPE_OPTIONS: {
  label: string;
  value: ComplianceRegistrationUsageType;
}[] = [
  {
    label: "Dialer",
    value: ComplianceRegistrationUsageType.Dialer,
  },
  {
    label: "Texter",
    value: ComplianceRegistrationUsageType.Texter,
  },
];

export const InternalTollFreeRegistrationForm = ({
  handleSubmitToStaff,
}: {
  handleSubmitToStaff: (
    values: Omit<
      CreateTollFreeRegistrationMutationVariables["input"],
      "subaccountId"
    >
  ) => Promise<{
    errors: Error[] | string[] | undefined | null;
  }>;
}): ReactElement => {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const subaccountContext = useSubaccount();

  const { phraseList: optOutPhrases, loading: optOutPhrasesLoading } =
    useOptOutPhraseList();

  const tollFreeRegistrationCvTokenRequired = useFeatureFlag(
    "tollFreeRegistrationCvTokenRequired"
  );
  const tollFreeRegistrationEinRequired = useFeatureFlag(
    "tollFreeRegistrationEinRequired"
  );

  const currentUser = useCurrentUser();
  const isStaff = useCurrentUserIsStaff();
  const isProd = import.meta.env.STW_ENV_NAME === "production";

  const allowTestPresetUse = isStaff && !isProd;

  const [testPreset, setTestPreset] = useState<string>("default");
  const TEST_PRESETS = [
    {
      label: "Test Preset 1: Political Organization",
      value: "test-preset-1",
      data: {
        organizationName: `${currentUser?.fullName || "Test"}'s Test Organization`,
        orgType: TollFreeOrgType.Political,
        displayName: `${currentUser?.fullName || "Test"}'s Test Org`,
        ein: "00-0000001",
        cvToken:
          "cv|1.0|mno|tfree|b3419fb8-d68c-4d23-bdcc-6a05c81a319a|aBcDeFg",
        address1: "123 Street St",
        address2: "",
        city: "City",
        state: "AK",
        postalCode: "10025",
        country: "US",
        website: "https://www.scaletowin.com",

        contactFirstName: `${currentUser?.fullName.split(" ")[0] || "Test"}`,
        contactLastName: `${currentUser?.fullName.split(" ").slice(1).join(" ") || "User"}`,
        contactEmail: currentUser?.email || "test@scaletowin.com",
        contactPhone: "212-555-1212",

        orgContactIsPrimaryContact: true,

        primaryContactFirstName: "",
        primaryContactLastName: "",
        primaryContactEmail: "",
        primaryContactPhone: "",

        createComplianceBundle: true,
        acknowledgedCompliance: false,

        messageVolume: 100,
        useCaseSummary: "Sending some texts!",
        productionMessageContent: "Hello there! Stop to quit",
        usageTypes: [ComplianceRegistrationUsageType.Texter],
        privacyPolicyUrl: "scaletowin.com/privacy",
        optInPageUrl: "scaletowin.com/signup",
        termsAndConditionsUrl: "scaletowin.com/terms",

        notes: "Some notes here",
        [submitErrorField]: null,
      },
    },
    {
      label: "Test Preset 2: Private Organization",
      value: "test-preset-2",
      data: {
        organizationName: `${currentUser?.fullName || "Test"}'s Test Organization`,
        orgType: TollFreeOrgType.Private,
        displayName: `${currentUser?.fullName || "Test"}'s Test Org`,
        ein: "00-0000001",
        cvToken: "",
        address1: "123 Street St",
        address2: "",
        city: "City",
        state: "AK",
        postalCode: "10025",
        country: "US",
        website: "https://www.scaletowin.com",

        contactFirstName: `${currentUser?.fullName.split(" ")[0] || "Test"}`,
        contactLastName: `${currentUser?.fullName.split(" ").slice(1).join(" ") || "User"}`,
        contactEmail: currentUser?.email || "test@scaletowin.com",
        contactPhone: "212-555-1212",

        orgContactIsPrimaryContact: true,

        primaryContactFirstName: "",
        primaryContactLastName: "",
        primaryContactEmail: "",
        primaryContactPhone: "",

        createComplianceBundle: true,
        acknowledgedCompliance: false,

        messageVolume: 100,
        useCaseSummary: "Sending some texts!",
        productionMessageContent: "Hello there! Stop to quit",
        usageTypes: [
          ComplianceRegistrationUsageType.Texter,
          ComplianceRegistrationUsageType.Dialer,
        ],
        privacyPolicyUrl: "scaletowin.com/privacy",
        optInPageUrl: "scaletowin.com/signup",
        termsAndConditionsUrl: "scaletowin.com/terms",

        notes: "Some notes here",
        [submitErrorField]: null,
      },
    },
  ];

  if (optOutPhrasesLoading) {
    return <Loading />;
  }

  return (
    <PageContainer>
      <PageHeader>Toll-Free Registration</PageHeader>
      <Typography variant="h2" sx={{ pb: 2 }}>
        Read about the form requirements{" "}
        <ExternalLink href="https://support.scaletowin.com/how-to-register-for-toll-free">
          here
        </ExternalLink>
        . Have questions?{" "}
        <ExternalLink href="https://support.scaletowin.com/contact">
          Feel free to reach out to us!
        </ExternalLink>
      </Typography>
      <AccentBox sx={{ px: 10, py: 5 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={tollFreeRegistrationSchema(optOutPhrases, {
            tollFreeRegistrationCvTokenRequired,
            tollFreeRegistrationEinRequired,
          })}
          onSubmit={async (
            { _submitError, ...values },
            { setFieldError, resetForm }
          ) => {
            // optimistic update to make sure RouteLeavingGuard
            // doesn't trigger if the submit succeeds
            resetForm({
              values: {
                ...values,
                [submitErrorField]: null,
              },
            });

            const { errors } = await handleSubmitToStaff({
              ...values,
              orgType: values.orgType as TollFreeOrgType,
            });

            if (errors) {
              // for RouteLeavingGuard
              setFieldError(
                submitErrorField,
                typeof errors[0] === "string" ? errors[0] : errors[0].message
              );
              snackbar.enqueueSnackbar("Error submitting registration", {
                variant: "error",
              });
            } else {
              snackbar.enqueueSnackbar("Registration submitted for review", {
                variant: "success",
              });

              if (subaccountContext?.subaccountLink) {
                await navigate(
                  subaccountContext.subaccountLink(`/phone-numbers`)
                );
              }
            }
          }}
        >
          {({
            values,
            touched,
            dirty,
            isSubmitting,
            submitForm,
            setValues,
            setFieldValue,
            errors,
            resetForm,
            handleBlur,
          }) => {
            return (
              <>
                <RouteLeavingGuard when={dirty} />

                <FlexBox flexDirection="column" sx={{ gap: 1 }}>
                  <FormikErrorBanner />

                  {allowTestPresetUse ? (
                    <InputRow>
                      <div />
                      <Box sx={{ mb: 2 }}>
                        <InputLabel variant="standard" id="test-preset-label">
                          Test Presets
                        </InputLabel>
                        <Select
                          fullWidth
                          labelId="test-preset-label"
                          value={testPreset}
                          onChange={async e => {
                            const selectedPreset = TEST_PRESETS.find(
                              preset => preset.value === e.target.value
                            );
                            if (selectedPreset?.data) {
                              setTestPreset(e.target.value);
                              await setValues(selectedPreset.data);
                            }
                          }}
                        >
                          <MenuItem value="default">
                            Select a test preset
                          </MenuItem>
                          {TEST_PRESETS.map(preset => (
                            <MenuItem key={preset.value} value={preset.value}>
                              {preset.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </InputRow>
                  ) : (
                    <div />
                  )}

                  <Typography
                    variant="h2Bold"
                    component="div"
                    sx={{ pb: 1, fontWeight: 600 }}
                  >
                    Message Sender Information
                  </Typography>

                  <InputRow>
                    <FormikTextField
                      label="Organization Name"
                      name="organizationName"
                      fast
                    />
                    <FormikSelect
                      label="Organization Type"
                      name="orgType"
                      withNoneOption
                      fast
                    >
                      {ORG_TYPE_MENU_OPTIONS.map(type => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </FormikSelect>
                  </InputRow>
                  <InputRow>
                    <FormikTextField
                      label="Doing Business As (DBA) (optional)"
                      name="displayName"
                      fast
                    />
                    <FormikTextField
                      label={`EIN${tollFreeRegistrationEinRequired ? "" : " (optional)"}`}
                      name="ein"
                      fast
                      onBlur={async e => {
                        const numsOnly = e.target.value.replace("-", "");
                        const newValue = numsOnly
                          ? `${numsOnly.slice(0, 2)}-${numsOnly.slice(2)}`
                          : "";
                        await setFieldValue("ein", newValue);
                        handleBlur(e);
                      }}
                    />
                  </InputRow>
                  {values.orgType === "POLITICAL" && (
                    <InputRow>
                      <FormikTextField
                        label={`Campaign Verify Token${tollFreeRegistrationCvTokenRequired ? "" : " (optional)"}`}
                        name="cvToken"
                        fast
                        multiline
                        minRows={3}
                        onBlur={async e => {
                          await setFieldValue("cvToken", e.target.value.trim());
                          handleBlur(e);
                        }}
                      />
                      <Typography
                        component="div"
                        sx={{
                          width: "48%",
                          alignSelf: "center",
                        }}
                      >
                        {tollFreeRegistrationCvTokenRequired ? (
                          <>
                            527s (political committees) must have an identity
                            token from Campaign Verify to apply.{" "}
                            <ExternalLink href="https://support.scaletowin.com/campaign-verify-registration-guide">
                              Click here to learn more about how to get verified
                              through Campaign Verify.
                            </ExternalLink>
                          </>
                        ) : (
                          <>
                            Starting February 17, 2026, all 527s (political
                            committees) will require a unique Toll-Free token
                            from Campaign Verify in order to apply for toll-free
                            numbers.{" "}
                            <ExternalLink href="https://support.scaletowin.com/campaign-verify-registration-guide">
                              Click here to learn more about how to get verified
                              through Campaign Verify.
                            </ExternalLink>
                          </>
                        )}
                      </Typography>
                    </InputRow>
                  )}

                  <InputRow>
                    <FormikTextField label="Street One" name="address1" fast />
                    <FormikTextField
                      label="Street Two (optional)"
                      name="address2"
                      fast
                    />
                  </InputRow>

                  <FlexBox gap={4} sx={{ width: "100%" }}>
                    <FormikTextField
                      label="City"
                      name="city"
                      fast
                      fullWidth
                      sx={{ flexGrow: 1 }}
                    />
                    <FormikSearchableSelect
                      label="State"
                      name="state"
                      fast
                      options={STATE_MENU_OPTIONS.map(option => {
                        return { label: option.label, id: option.value };
                      })}
                      fullWidth
                      sx={{
                        flexGrow: 1,
                        ".MuiInputBase-root:focus, .MuiInputBase-root:focus-within":
                          {
                            padding: 0,
                          },
                      }}
                    />
                    <FormikTextField
                      label="Postal Code"
                      name="postalCode"
                      fast
                      fullWidth
                      sx={{ flexGrow: 1 }}
                    />
                  </FlexBox>

                  <Divider sx={{ pt: 2, mb: 3 }} />
                  <Box>
                    <Typography
                      variant="h2Bold"
                      component="div"
                      sx={{ pb: 1, fontWeight: 600 }}
                    >
                      Website Requirements
                    </Typography>

                    <Typography variant="h2">
                      In order to qualify for toll-free, you must have:
                    </Typography>
                    <ul>
                      <li>
                        <Typography variant="h2">
                          An{" "}
                          <Typography variant="h2Bold">
                            active website.
                          </Typography>{" "}
                          Websites can be social media pages. Google Forms and
                          ActBlue pages are no longer accepted as websites.
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="h2">
                          An{" "}
                          <Typography variant="h2Bold">opt in form</Typography>{" "}
                          where users can sign up to receive texts from you
                          (this can be on your main website or its own webpage).
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="h2">
                          A{" "}
                          <Typography variant="h2Bold">
                            privacy policy
                          </Typography>{" "}
                          linked to the opt in form.
                        </Typography>
                      </li>
                    </ul>

                    <Typography variant="h2">
                      Read about the requirements{" "}
                      <ExternalLink href="https://support.scaletowin.com/how-to-register-for-toll-free">
                        here.
                      </ExternalLink>{" "}
                      Unless these elements are compliant with carrier policies,
                      your registration will be rejected.
                    </Typography>
                  </Box>
                  <FormikTextField name="website" label="Website" fast />

                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="acknowledgedCompliance"
                          value={values.acknowledgedCompliance}
                          checked={!!values.acknowledgedCompliance}
                          onChange={async e => {
                            await setFieldValue(
                              "acknowledgedCompliance",
                              !!e.target.checked
                            );
                          }}
                        />
                      }
                      label={
                        <Typography variant="h2Bold" sx={{ pt: 1 }}>
                          By checking this box, you acknowledge that you are
                          required to have a text message opt in form, website
                          terms & conditions and privacy policy that adheres to,
                          or is substantially similar to the toll-free
                          registration requirements.
                        </Typography>
                      }
                    />
                    {touched.acknowledgedCompliance &&
                      errors.acknowledgedCompliance && (
                        <Typography sx={{ color: palette.red }}>
                          {errors.acknowledgedCompliance}
                        </Typography>
                      )}
                  </FormGroup>

                  <Typography variant="h2" sx={{ pt: 1 }}>
                    Would you like Scale to Win to generate sample sign-up,
                    terms & conditions and privacy policy pages that meet these
                    requirements for you and submit them on your behalf?
                  </Typography>

                  <RadioGroup
                    row={true}
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                    }}
                    value={values.createComplianceBundle}
                    onChange={async e => {
                      await setFieldValue(
                        "createComplianceBundle",
                        e.target.value === "true"
                      );
                    }}
                    name="createComplianceBundle"
                  >
                    <FormControlLabel
                      value={true}
                      control={<Radio />}
                      label="Yes"
                    />

                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FlexBox>

                {!values.createComplianceBundle && (
                  <Box sx={{ py: 2 }}>
                    <CollapsibleDrawer
                      title="Registration Requirements"
                      startOpen
                      variant="slim"
                      sxHeader={{
                        backgroundColor: palette.midGray,
                        py: 0.5,
                        px: 2,
                      }}
                      sxHeading={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: palette.navy,
                      }}
                    >
                      <FlexBox
                        direction="column"
                        gap={3}
                        sx={{
                          border: `1px solid ${palette.midGray}`,
                          background: palette.lightestGray,
                          p: 3,
                        }}
                      >
                        <Box sx={{ fontSize: "16px" }}>
                          <Typography
                            variant="h2Bold"
                            component="div"
                            sx={{ pb: 1, fontWeight: 600 }}
                          >
                            Website Requirements
                          </Typography>

                          <Typography>Your website:</Typography>
                          <ul>
                            <li>Must be active (publicly accessible).</li>
                            <li>Must have content.</li>
                            <li>Must have a privacy policy.</li>
                            <li>Must have terms and conditions.</li>
                            <li>
                              Can be social media page. Google Forms and ActBlue
                              pages are no longer accepted as websites.
                            </li>
                          </ul>

                          <Typography variant="h2">
                            If contacts can sign up for texts from you on a
                            website, <u>it must have these elements</u>.
                          </Typography>
                          <ul>
                            <li>
                              An optional field for users to enter their phone
                              number
                            </li>
                            <li>
                              Explanatory language beneath, i.e. “By providing
                              your phone number you are agreeing to receive
                              texts at a maximum of X times a WEEK/DAY/MONTH
                              from ORGANIZATION. Message and data rates may
                              apply. Text HELP for more information. Text STOP
                              to stop receiving messages. Terms [LINK] and
                              Privacy [LINK]
                            </li>
                          </ul>
                          <ExternalLink href="https://support.scaletowin.com/how-to-register-for-toll-free">
                            Read more about carrier requirements here.
                          </ExternalLink>
                        </Box>

                        <Box>
                          <Typography
                            variant="h2Bold"
                            component="div"
                            sx={{ fontWeight: 600 }}
                          >
                            Terms and Conditions Requirements
                          </Typography>

                          <ul>
                            <li>
                              Must contain the sending organization's name.
                            </li>
                            <li>
                              Must specify the types of messages being sent. If
                              donations are included, must state, “donation
                              asks”.
                            </li>
                            <li>
                              Must disclosure approximate message frequency (Msg
                              frequency varies, 2/msgs per week, etc.)
                            </li>
                            <li>
                              Must include a "Message and data rates may apply"
                              disclosure
                            </li>
                            <li>
                              Must include HELP information (text HELP for help,
                              email or phone number for support)
                            </li>
                            <li>
                              Must include STOP/opt-out information (text STOP
                              to stop)
                            </li>
                            <li>Must include a link to the Privacy Policy</li>
                          </ul>
                          <FormikTextField
                            fullWidth
                            label="Your Terms and Conditions Page"
                            name="termsAndConditionsUrl"
                          />
                        </Box>

                        <Box sx={{ fontSize: "16px" }}>
                          <Typography
                            variant="h2Bold"
                            component="div"
                            sx={{ pb: 1, fontWeight: 600 }}
                          >
                            Privacy Policy Requirements
                          </Typography>
                          <Typography variant="h2">
                            Carriers are strict about how mobile user data is
                            handled.
                          </Typography>
                          <Typography variant="h2">
                            <ul>
                              <li>
                                You can only share mobile data for essential
                                services or if required by law.
                              </li>
                              <li>
                                You cannot share mobile data with affiliates or
                                similar campaigns.
                              </li>
                            </ul>
                          </Typography>
                          <FlexBox direction="column" gap={2}>
                            <Typography variant="h2" component="div">
                              Your privacy policy must clearly state this.
                            </Typography>
                            <Typography variant="h2" component="div">
                              You can make this clear on your privacy policy
                              with language such as:
                            </Typography>
                            <Typography
                              variant="h2"
                              component="div"
                              sx={{ fontStyle: "italic", pl: 3 }}
                            >
                              Text messaging originator opt in data and consent
                              will not be shared with any third parties,
                              provided that the foregoing does not apply to
                              sharing (1) with vendors, consultants and other
                              service providers who need access to such
                              information to carry out work on our behalf (and
                              who will not use such information for their own
                              purposes); (2) if we believe disclosure is
                              required by any applicable law, rule, or
                              regulation or to comply with law enforcement or
                              legal process.
                            </Typography>
                            <FormikTextField
                              fullWidth
                              label="Privacy Policy Page"
                              name="privacyPolicyUrl"
                            />
                          </FlexBox>
                        </Box>

                        <Box>
                          <Typography
                            variant="h2Bold"
                            component="div"
                            sx={{ fontWeight: 600 }}
                          >
                            Opt In Form Requirements
                          </Typography>

                          <Box>
                            <Typography variant="h2">
                              In order to qualify for toll-free, our vendor
                              wants to see that you have an SMS opt in page. It
                              is critical that your opt in page have these
                              things:
                            </Typography>

                            <Typography variant="h2">
                              <ul>
                                <li>
                                  Must have an optional field to enter a phone
                                  number.
                                </li>
                                <li>
                                  Must have an optional checkbox for the user to
                                  consent to SMS texts. Cannot have a
                                  pre-checked checkbox signing users up for
                                  texts.
                                </li>
                                <li>
                                  Must include a call-to-action explaining to
                                  the user they are signing up for texts.
                                </li>
                                <li>
                                  Must link to the terms and conditions and
                                  privacy policy.
                                </li>
                                <li>
                                  Eg. "By submitting this form and signing up
                                  for texts, you consent to receive marketing
                                  text messages (e.g. promos, cart reminders)
                                  from [Company Name] at the number provided,
                                  including messages sent by autodialer. Consent
                                  is not a condition of purchase. Msg & data
                                  rates may apply. Msg frequency varies.
                                  Unsubscribe at any time by replying STOP or
                                  clicking the unsubscribe link (where
                                  available). Reply HELP for help. Privacy
                                  Policy [link] & Terms [link]."
                                </li>
                              </ul>
                            </Typography>

                            <FormikTextField
                              name="optInPageUrl"
                              label="Opt In Page"
                              fast
                              fullWidth
                            />
                          </Box>
                        </Box>
                      </FlexBox>
                    </CollapsibleDrawer>
                  </Box>
                )}

                <Divider sx={{ pt: 2, mb: 3 }} />
                <Typography
                  variant="h2Bold"
                  component="div"
                  sx={{ pb: 1, fontWeight: 600 }}
                >
                  What kinds of messages will you send?
                </Typography>

                <Typography variant="h2" sx={{ pb: 2 }}>
                  Please describe your use case and the purpose of your messages
                  (i.e. political, fundraising, event reminders, etc.)
                </Typography>

                <InputRow>
                  <FormikTextField
                    multiline
                    name="useCaseSummary"
                    label="Description"
                    fast
                  />
                </InputRow>

                <FlexBox direction="column" gap={1}>
                  <Typography variant="h2">
                    Please provide 1-3 sample scripts. Your samples must include
                    the sender org name and opt out language, i.e. Stop to opt
                    out
                  </Typography>
                  <FormikTextField
                    name="productionMessageContent"
                    label="Sample messages"
                    fast
                    fullWidth
                    multiline
                  />
                </FlexBox>

                <InputRow>
                  <Box>
                    <Typography variant="h2" component="div" sx={{ pb: 1 }}>
                      Where will you use these toll-free numbers?
                    </Typography>
                    <FormGroup row>
                      {COMPLIANCE_REGISTRATION_USAGE_TYPE_OPTIONS.map(
                        option => (
                          <FormControlLabel
                            key={option.label}
                            control={
                              <Checkbox
                                name={`usageType-${option.value}`}
                                checked={values.usageTypes.includes(
                                  option.value
                                )}
                                onChange={async e => {
                                  if (e.target.checked) {
                                    await setFieldValue("usageTypes", [
                                      ...values.usageTypes,
                                      option.value,
                                    ]);
                                  } else {
                                    await setFieldValue(
                                      "usageTypes",
                                      values.usageTypes.filter(
                                        type => type !== option.value
                                      )
                                    );
                                  }
                                }}
                              />
                            }
                            label={option.label}
                          />
                        )
                      )}
                    </FormGroup>
                    {touched.usageTypes && errors.usageTypes && (
                      <Typography sx={{ color: palette.red }}>
                        {errors.usageTypes as string}
                      </Typography>
                    )}
                  </Box>

                  <FormikSelect
                    label="Expected Texts per Month"
                    name="messageVolume"
                    withNoneOption
                    noneOptionLabel="Expected texts..."
                    fast
                  >
                    {[
                      100, 1000, 10000, 100000, 250000, 500000, 750000, 1000000,
                      5000000, 10000000,
                    ].map(n => (
                      <MenuItem key={n} value={n}>
                        {n.toLocaleString()}
                      </MenuItem>
                    ))}
                  </FormikSelect>
                  <div />
                </InputRow>

                <Divider sx={{ pt: 2, mb: 3 }} />

                <InputRow>
                  <Box>
                    <Typography variant="h2Bold" sx={{ fontWeight: 600 }}>
                      Anticipated Date of Use
                    </Typography>
                    <Typography variant="h2" component="div" sx={{ py: 1 }}>
                      When will you start using these numbers?
                    </Typography>
                    <InputRow>
                      <FormikDatePicker
                        label="Requested Start Date"
                        name="usageStartDate"
                        fast
                        minDate={findEarliestStartDate()}
                        inputLabelProps={{
                          sx: { marginBottom: "-.25rem", marginTop: ".5rem" },
                        }}
                        fullWidth
                        helperTextProps={{
                          sx: { marginLeft: "14px", fontSize: ".75xem" },
                        }}
                        helperText="Start date must be 3 or more business days in the future"
                      />
                    </InputRow>
                  </Box>
                </InputRow>

                <Divider sx={{ pt: 2, mb: 3 }} />

                <FlexBox gap={1} direction="column">
                  <Typography
                    variant="h2Bold"
                    component="div"
                    sx={{ pb: 1, fontWeight: 600 }}
                  >
                    Contact Details
                  </Typography>

                  <Typography variant="h2" component="div">
                    Use a contact from the organization sending the texts. For
                    example, if the texts are from "Jane's campaign," the
                    contact should be someone from that campaign. This is just
                    to confirm the group's identity. If the person Scale to Win
                    should contact with questions isn't from the organization,
                    add them in the “Primary Contact” section below.
                  </Typography>
                  <Typography variant="h2" component="div" sx={{ pb: 1 }}>
                    Important: The email should match the organization's website
                    domain. Gmail can work only if the org's name is clearly
                    mentioned somewhere.
                  </Typography>

                  <InputRow>
                    <FormikTextField
                      label="Organization Contact First Name"
                      name="contactFirstName"
                      fast
                    />
                    <FormikTextField
                      label="Organization Contact Last Name"
                      name="contactLastName"
                      fast
                    />
                  </InputRow>

                  <InputRow>
                    <FormikTextField
                      label="Organization Contact Email"
                      name="contactEmail"
                      fast
                    />

                    <FormikTextField
                      label="Organization Contact Phone"
                      name="contactPhone"
                      fast
                    />
                  </InputRow>

                  <InputRow>
                    <Typography variant="h2">
                      Is the organization contact the person Scale to Win should
                      reach out to with questions about this registration?
                    </Typography>

                    <RadioGroup
                      row={true}
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                      }}
                      value={values.orgContactIsPrimaryContact}
                      onChange={async e => {
                        await setFieldValue(
                          "orgContactIsPrimaryContact",
                          e.target.value === "true"
                        );
                      }}
                      name="orgContactIsPrimaryContact"
                    >
                      <FormControlLabel
                        value={true}
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value={false}
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                  </InputRow>
                  {values.orgContactIsPrimaryContact ? null : (
                    <>
                      <InputRow>
                        <FormikTextField
                          label="Primary Contact First Name"
                          name="primaryContactFirstName"
                          fast
                        />
                        <FormikTextField
                          label="Primary Contact Last Name"
                          name="primaryContactLastName"
                          fast
                        />
                      </InputRow>
                      <InputRow>
                        <FormikTextField
                          label="Primary Contact Email"
                          name="primaryContactEmail"
                          fast
                        />

                        <FormikTextField
                          label="Primary Contact Phone"
                          name="primaryContactPhone"
                          fast
                        />
                      </InputRow>
                    </>
                  )}
                  <InputRow>
                    <FormikTextField
                      multiline
                      label="Anything you would like to share with Scale to Win staff? (optional)"
                      name="notes"
                      fast
                    />
                  </InputRow>
                </FlexBox>

                <FlexBox gap={2} justifyContent="flex-end" sx={{ mt: 1 }}>
                  <LinkButton
                    onClick={() => {
                      setTestPreset("default");
                      resetForm();
                    }}
                  >
                    Reset Form
                  </LinkButton>
                  <Button
                    variant="contained"
                    disabled={isSubmitting}
                    onClick={submitForm}
                  >
                    Submit for Review
                  </Button>
                </FlexBox>
              </>
            );
          }}
        </Formik>
      </AccentBox>
    </PageContainer>
  );
};

export default function TollFreeRegistration(): ReactElement {
  const { subaccount } = useSubaccount()!;
  const { slug } = useTenant()!;

  const tollFreeInApp = useFeatureFlag("tollFreeRegistrationInApp");

  const [createTollFreeRegistration] = useMutation<
    CreateTollFreeRegistrationMutation,
    CreateTollFreeRegistrationMutationVariables
  >(CREATE_TOLL_FREE_REGISTRATION, {
    onErrorSnackbarMessage: "Failed to create toll free registration",
  });

  if (tollFreeInApp) {
    return (
      <InternalTollFreeRegistrationForm
        handleSubmitToStaff={async input => {
          const { errors } = await createTollFreeRegistration({
            variables: {
              input: {
                ...input,
                subaccountId: subaccount.id,
              },
            },
          });
          if (errors) {
            return { errors: errors as unknown as string[] };
          }
          return { errors: null };
        }}
      />
    );
  } else {
    return (
      <Jotform
        formId={VERIFIED_TOLLFREE_JOTFORM_FORM_ID}
        urlParams={{
          teamName: subaccount.name,
          teamId: subaccount.slug,
          subdomain: slug,
        }}
      />
    );
  }
}
